import { useState } from 'react';
import { Search, Plus, ArrowRight, AlertCircle, AlertTriangle, Eye, ClipboardCheck, Cloud, Server, CheckCircle2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { aiModels, classificationEvidenceRecords } from '@/data/mockData';
import GradeTooltip from '@/components/GradeTooltip';

const RiskBadge = ({ tier }) => {
  const cls = tier === 'HIGH' ? 'risk-high' : tier === 'MEDIUM' ? 'risk-medium' : 'risk-low';
  return <span className={`${cls} text-[10px] font-mono px-2 py-0.5 rounded-full inline-block`}>{tier}</span>;
};

const StatusBadge = ({ status }) => {
  if (status === 'Compliant') return <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">Compliant</span>;
  if (status === 'Gap Detected') return <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">Gap Detected</span>;
  return <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20">Unaudited</span>;
};

function ComplianceBar({ covered, total, label }) {
  const pct = Math.round((covered / total) * 100);
  const color = pct === 100 ? '#10B981' : pct >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-[#94A3B8] w-24 flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-[#1E2A45] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono text-[#94A3B8] w-10 text-right">{covered}/{total}</span>
    </div>
  );
}

function LineageNode({ node }) {
  const isUnverified = node.status === 'unverified';
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${
      isUnverified
        ? 'bg-[#F59E0B]/5 border-[#F59E0B]/30 text-[#F59E0B]'
        : 'bg-[#0F1629] border-[#1E2A45] text-[#94A3B8]'
    }`}>
      <div className={`w-1.5 h-1.5 rounded-full ${isUnverified ? 'bg-[#F59E0B]' : 'bg-[#10B981]'}`} />
      <span>{node.name}</span>
      <span className="text-[10px] text-[#475569]">{node.type}</span>
    </div>
  );
}

export default function AIInventory() {
  const [selectedModel, setSelectedModel] = useState(null);
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [assuranceTooltip, setAssuranceTooltip] = useState(null);

  const filtered = aiModels.filter((m) => {
    const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase()) || m.workflow.toLowerCase().includes(search.toLowerCase());
    const matchesRisk = riskFilter === 'all' || m.riskTier === riskFilter;
    return matchesSearch && matchesRisk;
  });

  return (
    <div data-testid="ai-inventory-page" className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input
              data-testid="inventory-search"
              type="text"
              placeholder="Search models or workflows..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none focus:border-[#6366F1] transition-colors"
            />
          </div>
          <select
            data-testid="risk-filter"
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
            className="px-3 py-2 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#94A3B8] focus:outline-none focus:border-[#6366F1]"
          >
            <option value="all">All Risk Tiers</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>
        <button data-testid="register-model-btn" onClick={() => toast.success('Model registration wizard opened')} className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-sm font-medium px-4 py-2 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
          <Plus size={16} /> Register Model
        </button>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1E2A45]">
                {['Model', 'Version', 'Workflow', 'Risk Tier', 'Compliance', 'Assurance Readiness', 'Records', 'Last Audited', 'Actions'].map((h) => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((model) => (
                <tr
                  key={model.id}
                  data-testid={`model-row-${model.id}`}
                  onClick={() => setSelectedModel(model)}
                  className="border-b border-[#1E2A45]/50 table-row-interactive"
                >
                  <td className="py-3 px-4">
                    <div className="text-[#F8FAFC] font-medium">{model.name}</div>
                    <div className="text-[10px] text-[#475569]">{model.provider}</div>
                    {model.infraSource && (
                      <div className="flex items-center gap-1 mt-0.5 text-[9px] text-[#475569]">
                        {model.infraType === 'server' ? <Server size={9} /> : <Cloud size={9} />}
                        <span>{model.infraSource}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 font-mono text-[#94A3B8] text-xs">{model.version}</td>
                  <td className="py-3 px-4 text-[#94A3B8]">{model.workflow}</td>
                  <td className="py-3 px-4"><RiskBadge tier={model.riskTier} /></td>
                  <td className="py-3 px-4"><StatusBadge status={model.complianceStatus} /></td>
                  <td className="py-3 px-4 relative">
                    {model.assurance ? (
                      <div className="flex items-center gap-2"
                        onMouseEnter={(e) => { e.stopPropagation(); setAssuranceTooltip(model.id); }}
                        onMouseLeave={() => setAssuranceTooltip(null)}
                      >
                        <div className="w-16 h-1.5 rounded-full bg-[#1E2A45] overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${model.assurance.readiness}%`, backgroundColor: model.assurance.readiness >= 80 ? '#10B981' : model.assurance.readiness > 0 ? '#F59E0B' : '#EF4444' }} />
                        </div>
                        <span className="text-[10px] font-mono" style={{ color: model.assurance.readiness >= 80 ? '#10B981' : model.assurance.readiness > 0 ? '#F59E0B' : '#EF4444' }}>
                          {model.assurance.readiness}%
                        </span>
                        {model.assurance.readiness === 0 && <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20">Unaudited</span>}
                        {model.assurance.readiness > 0 && model.assurance.readiness < 70 && <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">Gap</span>}
                        {assuranceTooltip === model.id && (
                          <div className="absolute z-50 top-full left-0 mt-1 bg-[#0F1629] border border-[#1E2A45] rounded-lg p-3 shadow-xl min-w-[200px]" onClick={(e) => e.stopPropagation()}>
                            <div className="text-[10px] text-[#475569] uppercase mb-2 font-semibold">Assurance Breakdown</div>
                            <div className="space-y-1.5 text-[10px]">
                              <div className="flex justify-between"><span className="text-[#94A3B8]">Evidence coverage</span><span className="text-[#F8FAFC] font-mono">{model.assurance.evidence}%</span></div>
                              <div className="flex justify-between"><span className="text-[#94A3B8]">Change control coverage</span><span className="text-[#F8FAFC] font-mono">{model.assurance.changeControl}%</span></div>
                              <div className="flex justify-between"><span className="text-[#94A3B8]">Human review rate</span><span className="text-[#F8FAFC] font-mono">{model.assurance.humanReview}%</span></div>
                              <div className="flex justify-between"><span className="text-[#94A3B8]">Open control gaps</span><span className={`font-mono ${model.assurance.openGaps > 0 ? 'text-[#F59E0B]' : 'text-[#10B981]'}`}>{model.assurance.openGaps}</span></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : <span className="text-[10px] text-[#475569]">—</span>}
                  </td>
                  <td className="py-3 px-4 font-mono text-[#94A3B8]">{model.decisionRecords.toLocaleString()}</td>
                  <td className="py-3 px-4 text-[#64748B] text-xs">{model.lastAudited}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button data-testid={`view-model-${model.id}`} className="text-[#6366F1] hover:text-[#818cf8] transition-colors" onClick={(e) => { e.stopPropagation(); setSelectedModel(model); }}>
                        <Eye size={14} />
                      </button>
                      <button data-testid={`audit-model-${model.id}`} onClick={(e) => { e.stopPropagation(); toast.info(`Audit initiated for ${model.name}`); }} className="text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                        <ClipboardCheck size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Model Detail Drawer */}
      <Sheet open={!!selectedModel} onOpenChange={() => setSelectedModel(null)}>
        <SheetContent side="right" className="w-full sm:w-[420px] lg:w-[520px] bg-[#0F1629] border-l border-[#1E2A45] p-0 sm:max-w-[520px]">
          {selectedModel && (
            <div className="h-full flex flex-col">
              <SheetHeader className="p-6 border-b border-[#1E2A45]">
                <div className="flex items-start justify-between">
                  <div>
                    <SheetTitle className="text-[#F8FAFC] text-lg font-bold mb-1">{selectedModel.name}</SheetTitle>
                    <div className="text-xs text-[#64748B]">{selectedModel.provider} · {selectedModel.version}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <RiskBadge tier={selectedModel.riskTier} />
                    <StatusBadge status={selectedModel.complianceStatus} />
                  </div>
                </div>
              </SheetHeader>

              <Tabs defaultValue="overview" className="flex-1 flex flex-col">
                <TabsList className="bg-transparent border-b border-[#1E2A45] rounded-none px-6 h-auto py-0 justify-start gap-0">
                  {['Overview', 'Classification', 'Data Lineage', 'Compliance Controls', 'Decision Records'].map((tab) => (
                    <TabsTrigger
                      key={tab}
                      value={tab.toLowerCase().replace(/\s+/g, '-')}
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6366F1] data-[state=active]:text-[#6366F1] text-[#64748B] text-xs px-4 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      {tab}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <ScrollArea className="flex-1">
                  {/* Overview Tab */}
                  <TabsContent value="overview" className="p-6 space-y-5 mt-0">
                    <div>
                      <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Deployment</div>
                      <div className="text-sm text-[#94A3B8]">{selectedModel.deployment}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Purpose</div>
                      <div className="text-sm text-[#94A3B8]">{selectedModel.purpose}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Data Access</div>
                      <div className="text-sm text-[#94A3B8]">{selectedModel.dataAccess}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Owner</div>
                      <div className="text-sm text-[#94A3B8]">{selectedModel.owner}</div>
                    </div>
                    {selectedModel.riskFlags.length > 0 && (
                      <div>
                        <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-2">Risk Flags ({selectedModel.riskFlags.length} active)</div>
                        <div className="space-y-2">
                          {selectedModel.riskFlags.map((flag, i) => (
                            <div
                              key={i}
                              className={`flex items-start gap-2 p-3 rounded-lg text-xs ${
                                flag.type === 'critical' ? 'bg-[#EF4444]/5 border border-[#EF4444]/20' : 'bg-[#F59E0B]/5 border border-[#F59E0B]/20'
                              }`}
                            >
                              {flag.type === 'critical' ? <AlertCircle size={14} className="text-[#EF4444] flex-shrink-0 mt-0.5" /> : <AlertTriangle size={14} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />}
                              <span className={flag.type === 'critical' ? 'text-[#EF4444]' : 'text-[#F59E0B]'}>{flag.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Classification Tab */}
                  <TabsContent value="classification" className="p-6 space-y-4 mt-0">
                    {(() => {
                      const cer = classificationEvidenceRecords.find(c => c.modelId === selectedModel.id);
                      if (!cer) return <div className="text-xs text-[#475569]">No Classification Evidence Record available for this model.</div>;
                      const gc = cer.proofGrade === 'AUDIT-GRADE' ? '#10B981' : '#F59E0B';
                      return (
                        <>
                          <div className="rounded-xl border border-[#1E2A45] p-4" style={{ borderLeftWidth: 3, borderLeftColor: gc }}>
                            <div className="flex items-start justify-between mb-2 gap-3">
                              <div>
                                <div className="text-xs font-bold text-[#F8FAFC] uppercase tracking-wider">{cer.id}</div>
                                <div className="text-[9px] text-[#64748B]">Version {cer.version} · Effective: {cer.effectiveDate}</div>
                                <div className="text-[9px] text-[#475569]">Approved by: {cer.approvedBy} · Reconstruction: {cer.reconstructionMethod}</div>
                              </div>
                              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                <GradeTooltip grade={cer.proofGrade}>
                                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border cursor-help" style={{ color: gc, borderColor: gc + '30', background: gc + '10' }}>◆ {cer.proofGrade}</span>
                                </GradeTooltip>
                                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#F59E0B]" data-testid="legal-classification-badge">{cer.classification}</span>
                              </div>
                            </div>
                            <div className="space-y-1.5 mt-3">
                              {[
                                { label: 'EU AI Act Annex III Category', value: cer.annexCategory },
                                { label: 'Organizational Role', value: `${cer.orgRole} ${cer.confirmed ? '✅ Confirmed' : ''}` },
                                { label: 'Jurisdiction Scope', value: cer.jurisdiction },
                                { label: 'Human Oversight Required', value: `${cer.humanOversight} ${cer.humanOversight.includes('MANDATORY') ? '✅ Met' : ''}` },
                                { label: 'Documentation Required', value: `${cer.docRequired} ${cer.docCurrent ? '✅ Current' : '⚠ Incomplete'}` },
                                { label: 'Last Legal Review', value: cer.lastReview },
                                { label: 'Next Review Due', value: cer.nextReview },
                              ].map(row => (
                                <div key={row.label} className="flex items-start gap-2 text-[11px]">
                                  <span className="text-[#94A3B8] w-44 flex-shrink-0">{row.label}:</span>
                                  <span className="text-[#F8FAFC]">{row.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          {cer.driftAlert && (
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20">
                              <AlertTriangle size={13} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="text-[10px] font-semibold text-[#F59E0B]">POTENTIAL LEGAL STATUS REVIEW REQUIRED</div>
                                <div className="text-[10px] text-[#94A3B8] mt-0.5">{cer.driftAlert}</div>
                              </div>
                            </div>
                          )}
                          {!cer.driftAlert && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-[#10B981]/5 border border-[#10B981]/20 text-[10px] text-[#10B981]">
                              <CheckCircle2 size={12} /> Classification current and compliant. No legal status drift detected.
                            </div>
                          )}
                          {/* Version History Timeline */}
                          <div>
                            <div className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-2">Classification Version History</div>
                            <div className="space-y-1.5">
                              {cer.versionHistory.map((v, i) => (
                                <div key={v.v} className="flex items-center gap-3 text-[10px]">
                                  <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-[#6366F1]' : 'bg-[#1E2A45]'}`} />
                                  <span className="font-mono text-[#F8FAFC] w-8">{v.v}</span>
                                  <span className="text-[#475569] w-16">{v.date}</span>
                                  <span className="text-[#94A3B8]">{v.summary}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 pt-2">
                            <button onClick={() => toast.info('Starting classification review')} className="text-[10px] text-[#6366F1] hover:text-[#818cf8]">View CER →</button>
                            <button onClick={() => toast.info('Scheduling review')} className="text-[10px] text-[#94A3B8] hover:text-[#F8FAFC]">Schedule Review →</button>
                          </div>
                        </>
                      );
                    })()}
                  </TabsContent>

                  {/* Data Lineage Tab */}
                  <TabsContent value="data-lineage" className="p-6 mt-0">
                    <div className="space-y-2">
                      {selectedModel.dataLineage?.map((node, i) => (
                        <div key={i}>
                          <LineageNode node={node} />
                          {i < selectedModel.dataLineage.length - 1 && (
                            <div className="flex justify-center py-1">
                              <div className="w-px h-4 bg-[#1E2A45]" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  {/* Compliance Controls Tab */}
                  <TabsContent value="compliance-controls" className="p-6 space-y-4 mt-0">
                    <ComplianceBar covered={selectedModel.euAiAct.covered} total={selectedModel.euAiAct.total} label="EU AI Act" />
                    <ComplianceBar covered={selectedModel.iso42001.covered} total={selectedModel.iso42001.total} label="ISO 42001" />
                    <ComplianceBar covered={selectedModel.nistAiRmf.covered} total={selectedModel.nistAiRmf.total} label="NIST AI RMF" />
                    {(selectedModel.euAiAct.covered < selectedModel.euAiAct.total ||
                      selectedModel.iso42001.covered < selectedModel.iso42001.total ||
                      selectedModel.nistAiRmf.covered < selectedModel.nistAiRmf.total) && (
                      <div className="mt-4 pt-4 border-t border-[#1E2A45]">
                        <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-3">Uncovered Controls</div>
                        <div className="space-y-2">
                          {selectedModel.euAiAct.covered < selectedModel.euAiAct.total && (
                            <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#EF4444]/5 border border-[#EF4444]/10">
                              <span className="text-xs text-[#EF4444]">EU AI Act — {selectedModel.euAiAct.total - selectedModel.euAiAct.covered} gaps</span>
                              <span className="text-[10px] bg-[#EF4444]/10 text-[#EF4444] px-2 py-0.5 rounded">Gap</span>
                            </div>
                          )}
                          {selectedModel.iso42001.covered < selectedModel.iso42001.total && (
                            <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#EF4444]/5 border border-[#EF4444]/10">
                              <span className="text-xs text-[#EF4444]">ISO 42001 — {selectedModel.iso42001.total - selectedModel.iso42001.covered} gaps</span>
                              <span className="text-[10px] bg-[#EF4444]/10 text-[#EF4444] px-2 py-0.5 rounded">Gap</span>
                            </div>
                          )}
                          {selectedModel.nistAiRmf.covered < selectedModel.nistAiRmf.total && (
                            <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#EF4444]/5 border border-[#EF4444]/10">
                              <span className="text-xs text-[#EF4444]">NIST AI RMF — {selectedModel.nistAiRmf.total - selectedModel.nistAiRmf.covered} gaps</span>
                              <span className="text-[10px] bg-[#EF4444]/10 text-[#EF4444] px-2 py-0.5 rounded">Gap</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Decision Records Tab */}
                  <TabsContent value="decision-records" className="p-6 mt-0">
                    <div className="text-center py-8">
                      <div className="text-2xl font-bold text-[#F8FAFC] font-mono mb-1">{selectedModel.decisionRecords.toLocaleString()}</div>
                      <div className="text-xs text-[#64748B] mb-4">total records this month</div>
                      <button onClick={() => toast.info('Opening Decision Ledger with filter...')} className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1 mx-auto">
                        View in Decision Ledger <ArrowRight size={12} />
                      </button>
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
